import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdOutlineArrowBack } from 'react-icons/md';
import { Helmet } from 'react-helmet';
import GaugeComponent from 'react-gauge-component';
import { FaPerson } from 'react-icons/fa6';
import moment from 'moment';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import Button from 'components/Button2';
import ButtonLink from 'components/ButtonLink';
import LevelBar from 'components/LevelBar';
import useWindowSize from 'hooks/useWindowSize';
import 'katex/dist/katex.min.css';
import renderMathInElement from 'katex/dist/contrib/auto-render.js';

import './index.css';

// New SHSAT form: 50 questions per section (ELA 50 + Math 50 = 100 total),
// 180 minutes total. Per-question time targets used for the time-spent
// coloring and the Section Time Averages gauges.
const timePerQuestionFull = 180 * 60 / 100;
const timePerQuestionOne = 90 * 60 / 50;
const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

// Raw (0-50) -> scaled (0-350) conversion for one 50-question section.
// Shown in the tutor "Scaled Score" popup so tutors can see how a raw score
// maps to the estimated scaled score.
const SCALED_TABLE_50 = [
    0, 19, 36, 51, 64, 76, 87, 96, 104, 112,
    118, 124, 129, 134, 139, 143, 147, 150, 154, 157,
    160, 163, 166, 169, 172, 175, 178, 181, 184, 186,
    190, 193, 196, 200, 203, 207, 211, 216, 221, 226,
    232, 238, 246, 254, 263, 274, 286, 299, 314, 331,
    350,
];

type TestSHSATReportProps = {
    // Bulk print mounts this component directly instead of routing to it, so
    // the test id has to be able to arrive as a prop as well as a URL param.
    testIdOverride?: string;
    // Rendered inside the bulk-print container: drop the page chrome (document
    // title, action buttons) so the sheet carries report content only.
    embedded?: boolean;
    // Fires once this report's data has settled, so a bulk print can wait for
    // every report to arrive before opening the print dialog.
    onReady?: () => void;
};

function testSHSATReport({ testIdOverride, embedded = false, onReady }: TestSHSATReportProps = {}) {
    const [testData, setTestData] = useState(null);
    const [sectionIndex, setSectionIndex] = useState(0);
    const [moduleIndex, setModuleIndex] = useState(0);
    // Which Level 1 domain's Level 2 breakdown is expanded (click to open),
    // mirroring the SAT report.
    const [rootCategoryName, setRootCategoryName] = useState('');
    // Tutor-only: toggles the estimated scaled-score popup next to the score.
    const [showScaledInfo, setShowScaledInfo] = useState(false);

    const { user } = useAuthContext();
    const windowSize = useWindowSize();
    const navigate = useNavigate();
    const { testId: routeTestId } = useParams();
    const testId = testIdOverride ?? routeTestId;
    const printRef = useRef(null);

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const formatTime2 = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        return hours 
            ? `${hours} hr ${minutes} min ${seconds} sec`
            : minutes
            ? `${minutes} min ${seconds} sec`
            : `${seconds} sec`;
    }

    // Print the live DOM natively. The @media print stylesheet isolates the
    // report from the app shell, so the printout mirrors the on-screen overview
    // (gauges/score bars survive because nothing is cloned).
    const handlePrint = () => {
        // Scope print isolation to this page only: the @media print rules key
        // off this body class, so other pages' printing is never affected.
        document.body.classList.add('printing-shsat-test-report');
        // Remove the browser's default page margins for this print only, which
        // also drops the auto-added header/footer (date, title, URL, page #).
        // Injected here and removed on afterprint so other prints keep theirs.
        const pageStyle = document.createElement('style');
        pageStyle.textContent = '@page { margin: 0; }';
        document.head.appendChild(pageStyle);
        const cleanup = () => {
            document.body.classList.remove('printing-shsat-test-report');
            pageStyle.remove();
            window.removeEventListener('afterprint', cleanup);
        };
        window.addEventListener('afterprint', cleanup);
        window.print();
    };

    // `onReady` is read through a ref so a parent passing a fresh closure each
    // render cannot retrigger the fetch -- the effect stays keyed on the id.
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;

    useEffect(() => {
        if (!testId) return;
        let cancelled = false;
        axios.get(`tests/digital/shsatResultTutor/${testId}/`)
            .then(res => {
                if (cancelled) return;
                setTestData(res.data);
                onReadyRef.current?.();
            })
            .catch(error => {
                console.log(error);
                // Report ready even on failure: one unreachable report must not
                // leave a bulk print waiting for a dialog that never opens.
                if (!cancelled) onReadyRef.current?.();
            });
        return () => { cancelled = true; };
    }, [testId]);

    // KaTeX-render the question tables so math answers like ``$65^{\circ}$`` display
    // typeset instead of raw LaTeX. Re-runs when the data or the active
    // section/module changes (React re-renders the table to plain text on nav).
    useEffect(() => {
        if (!testData) return;
        const id = window.setTimeout(() => {
            // Scoped to this report's own subtree. A bulk print mounts several
            // reports at once, and a document-wide selector would let each
            // instance re-typeset its siblings' already-rendered KaTeX.
            const root: HTMLElement | Document = printRef.current ?? document;
            root.querySelectorAll('.testSHSATReport__questionsTable').forEach(el => {
                try {
                    renderMathInElement(el as HTMLElement, {
                        delimiters: [
                            { left: '$$', right: '$$', display: true },
                            { left: '$', right: '$', display: false },
                        ],
                        throwOnError: false,
                    });
                } catch (e) { /* ignore render errors */ }
            });
        }, 0);
        return () => window.clearTimeout(id);
    }, [testData, sectionIndex, moduleIndex]);

    if (!testData) return <></>;

    // Header cells and one answer row, shared by the on-screen tabbed table and
    // the print-only table further down. Those two tables used to hold
    // independent copies of this logic, and that is exactly how they drifted:
    // the print copy never grew the adaptive Tier column. One renderer now
    // feeds both, so they cannot disagree again. The row markup below is the
    // on-screen markup, moved -- not rewritten.
    const questionTableHead = (
        <tr>
            <th>Question</th>
            {testData.isTrueAdaptive && <th>Tier</th>}
            <th>Correct Answer</th>
            <th>Your Answer</th>
            <th>Category</th>
            {/* {user.customizations.find(customization => customization === 'Show Correct Rate') &&
                <th>Correct %</th>
            } */}
            <th>Time Spent</th>
        </tr>
    );

    const questionRow = (testModule, answer) => {
        // Look up the served tier from the ``adaptiveInfo.tierBySlot`` map the
        // tutor endpoint sends. Null for legacy fixed-form tests; the column
        // header is also gated on ``testData.isTrueAdaptive``.
        const moduleId = testModule?.moduleId ?? testModule?.id;
        // Django JSON-serializes integer dict keys as strings; the map arrives
        // keyed by stringified ids even though the server built it with int
        // keys. Coerce to string on lookup so we don't silently get
        // ``undefined`` for every row.
        const moduleIdKey = moduleId != null ? String(moduleId) : null;
        const slotKey = String(answer.questionNumber);
        const tier = testData.isTrueAdaptive && moduleIdKey != null
            ? testData.adaptiveInfo?.tierBySlot?.[moduleIdKey]?.[slotKey]
            : null;
        return (
                        <tr key={(testModule?.name ?? 'm') + ':' + answer.questionNumber}>
                            <td>{answer.questionNumber}</td>
                            {testData.isTrueAdaptive && (
                                <td>
                                    {tier
                                        ? <span
                                            title={`Served tier: ${tier}`}
                                            // Classes rather than an inline style so the print
                                            // stylesheet can slim the badge for its compact rows.
                                            className={tier === 'hard'
                                                ? 'testSHSATReport__tier testSHSATReport__tier--hard'
                                                : 'testSHSATReport__tier testSHSATReport__tier--medium'
                                            }
                                        >{tier === 'hard' ? 'H' : 'M'}</span>
                                        : '-'}
                                </td>
                            )}
                            <td>
                                {(() => {
                                    switch (answer.questionType) {
                                    case 1:
                                        return answer.correctChoice !== null
                                        ? MULTIPLE_CHOICES[answer.correctChoice]
                                        : "-";
                                    case 3:
                                        return answer.correctChoice?.length > 0
                                        ? answer.correctChoice.map(i => MULTIPLE_CHOICES[i]).join(", ")
                                        : "-";
                                    case 4:
                                        return answer.correctAnswers?.length > 0
                                        ? answer.correctAnswers.join(", ")
                                        : "-";
                                    case 5:
                                    case 6:
                                    case 7:
                                    case 8:
                                    case 9:
                                    case 10:
                                    case 11:
                                    case 12:
                                        return answer.questionType === 7 && answer.correctAnswers?.length > 0 ? answer.correctAnswers.join(", ") : "See review";
                                    default:
                                        return "-";
                                    }
                                })()}
                            </td>
                            <td>
                                {(() => {
                                    switch (answer.questionType) {
                                    case 1:
                                        return answer.choice !== null ? (
                                        answer.choice === answer.correctChoice ? (
                                            <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                        ) : (
                                            <p className="testSHSATReport__questionIncorrect">
                                            {MULTIPLE_CHOICES[answer.choice]}; Incorrect
                                            </p>
                                        )
                                        ) : (
                                        <p className="testSHSATReport__questionOmitted">Omitted</p>
                                        );
                                    case 3:
                                        return answer?.choice?.length > 0? (
                                        JSON.stringify([...answer.choice].sort()) === JSON.stringify([...answer.correctChoice].sort()) ? (
                                            <p>{answer.choice.map(i => MULTIPLE_CHOICES[i]).join(", ")}; Correct</p>
                                        ) : (
                                            <p className="testSHSATReport__questionIncorrect">
                                            {answer.choice.map(i => MULTIPLE_CHOICES[i]).join(", ")}; Incorrect
                                            </p>
                                        )
                                        ) : (
                                        <p className="testSHSATReport__questionOmitted">Omitted</p>
                                        );
                                    case 4:
                                        return answer?.studentAnswers?.length > 0? (
                                        answer.correctAnswers.length === answer.studentAnswers.length &&
                                        answer.correctAnswers.flat().every(item => answer.studentAnswers.flat().includes(item)) ? (
                                            <p>{answer.studentAnswers.join(", ")}; Correct</p>
                                        ) : (
                                            <p className="testSHSATReport__questionIncorrect">
                                            {answer.studentAnswers.join(", ")}; Incorrect
                                            </p>
                                        )
                                        ) : (
                                        <p className="testSHSATReport__questionOmitted">Omitted</p>
                                        );
                                    case 5:
                                        return (answer.isCorrect
                                            ? <p>Correct</p>
                                            : answer.studentAnswers.length > 0
                                            ? <p className="testSHSATReport__questionIncorrect">Incorrect</p>
                                            : <p className="testSHSATReport__questionOmitted">Omitted</p>
                                        )
                                    case 6:
                                        return (answer.isCorrect
                                            ? <p>Correct</p>
                                            : answer.studentAnswers.length > 0
                                            ? <p className="testSHSATReport__questionIncorrect">Incorrect</p>
                                            : <p className="testSHSATReport__questionOmitted">Omitted</p>
                                        )
                                    case 7:
                                        const allEmpty =
                                            Array.isArray(answer.studentAnswers) &&
                                            answer.studentAnswers.length > 0 &&
                                            answer.studentAnswers.every(([_, val]) => val === "");
                                        return (
                                            answer.isCorrect ? (
                                                <p>Correct</p>
                                            ) : allEmpty ? (
                                                <p className="testSHSATReport__questionOmitted">Omitted</p>
                                            ) : (
                                                <p className="testSHSATReport__questionIncorrect">Incorrect</p>
                                            )
                                        );
                                    case 8:
                                        return (answer.isCorrect
                                            ? <p>Correct</p>
                                            : answer.studentAnswers.length > 0
                                            ? <p className="testSHSATReport__questionIncorrect">Incorrect</p>
                                            : <p className="testSHSATReport__questionOmitted">Omitted</p>
                                        )
                                    case 9:
                                        return (answer.isCorrect
                                            ? <p>Correct</p>
                                            : answer.studentAnswers.length > 0
                                            ? <p className="testSHSATReport__questionIncorrect">Incorrect</p>
                                            : <p className="testSHSATReport__questionOmitted">Omitted</p>
                                        )
                                    case 10:
                                        return answer?.choice?.length > 0? (
                                        JSON.stringify([...answer.choice].sort()) === JSON.stringify([...answer.correctChoice].sort()) ? (
                                            <p>Correct</p>
                                        ) : (
                                            <p className="testSHSATReport__questionIncorrect">Incorrect</p>
                                        )
                                        ) : (
                                        <p className="testSHSATReport__questionOmitted">Omitted</p>
                                        );
                                    case 11:
                                    case 12:
                                        return (answer.isCorrect
                                            ? <p>Correct</p>
                                            : answer.studentAnswers
                                            ? <p className="testSHSATReport__questionIncorrect">Incorrect</p>
                                            : <p className="testSHSATReport__questionOmitted">Omitted</p>
                                        )
                                    default:
                                        return "-";
                                    }
                                })()}
                            </td>
                            <td>{answer.category ?? '-'}</td>
                            {/* {user.customizations.find(customization => customization === 'Show Correct Rate') &&
                                (answer.correctRate ? <td>{parseFloat(answer.correctRate).toFixed(1)}%</td> : <></>)
                            } */}
                            <td>
                                <p className={answer.timeSpent > (testData.testModules.length === 1
                                            ? timePerQuestionOne * 1.5
                                            : testData.testModules.length === 2
                                            ? timePerQuestionFull * 1.5
                                            : 60 * 1.5
                                        )
                                    ?   'testSHSATReport__timeSpentBad'
                                    :   answer.timeSpent > (testData.testModules.length === 1
                                            ? timePerQuestionOne
                                            : testData.testModules.length === 2
                                            ? timePerQuestionFull
                                            : 60
                                        )
                                    ?   'testSHSATReport__timeSpentWarning'
                                    :   'testSHSATReport__timeSpentGood'
                                }>
                                    {formatTime(answer.timeSpent)}
                                </p>
                            </td>
                        </tr>
        );
    };

    // The module the tabbed table is currently showing.
    const currentModule = testData.testModules.filter(testModule => (
        testModule.subject === testData.testSections[sectionIndex].subject
    ))[moduleIndex];

    // Every module in reading order -- each section, then each module within
    // it. Drives the print-only table so a printed report carries the whole
    // test instead of whichever tab happened to be open.
    const modulesInOrder = testData.testSections.flatMap(testSection => (
        testData.testModules.filter(testModule => testModule.subject === testSection.subject)
    ));

    const moduleLabel = (testModule) => {
        const siblings = testData.testModules.filter(m => m.subject === testModule.subject);
        if (siblings.length < 2) return testModule.subject;
        const hardness = testModule.moduleNumber === 2
            ? (testModule.hard ? ' (Hard)' : ' (Easy)')
            : '';
        return `${testModule.subject} \u2014 Module ${testModule.moduleNumber}${hardness}`;
    };

    // Raw->scaled conversion table for the Scaled Score popup. Use the table
    // the backend computed for THIS test's actual question count (so a 57-q
    // test shows the 57-row curve and matches the displayed scaled); fall back
    // to the static 50-row table only if the backend didn't send one.
    const scaledConv: number[] = testData.scaledConversion ?? SCALED_TABLE_50;
    const scaledConvMax: number = testData.scaledConversionMaxRaw ?? 50;
    const scaledConvHalf = Math.ceil((scaledConvMax + 1) / 2);

    return (
        <div className='testSHSATReport' ref={printRef}>
            {!embedded &&
                <Helmet>
                    <title>
                        Test Report - Test Center Admin
                    </title>
                </Helmet>
            }
            {!embedded && (
                <div className='testSHSATReport__bar'>
                    <button className='testSHSATReport__back' onClick={() => navigate(-1)}>
                        <MdOutlineArrowBack className='testSHSATReport__backIcon' />
                        Back
                    </button>
                </div>
            )}
            <div className='testSHSATReport__headerGroup'>
                <div className='testSHSATReport__headerSection'>
                    {testData.logo && 
                        <img
                            className='testSHSATReport__headerLogo'
                            alt='logo'
                            src={testData.logo}
                        />
                    }
                </div>
                <div className='testSHSATReport__headerSection'>
                    <div className='testSHSATReport__headerInfo'>
                        <p>
                            Name: {testData.studentName}
                        </p>
                        <p>
                            Email: {testData.email}
                        </p>
                        <p>
                            Test name: {testData.name}
                        </p>
                        <p>
                            Tested on: {moment(testData.testDate).format('MMMM D YYYY')}
                        </p>
                    </div>
                </div>
            </div>
            <div className='testSHSATReport__headerGroup'>
                <h1 className='testSHSATReport__headerTitle'>
                    Test Result Overview
                </h1>
                <div className='testSHSATReport__buttonGroup'>
                    <ButtonLink className='testSHSATReport__button--blue' to={`/review/digital-shsat/${testId}`}>
                        REVIEW QUESTIONS
                    </ButtonLink>
                    <Button className='testSHSATReport__button' secondary onClick={handlePrint}>
                        PRINT REPORT
                    </Button>
                    <ButtonLink className='testSHSATReport__button' to={`/students/${testData.studentId}/digital-shsat/performanceHistory`}>
                        PERFORMANCE HISTORY
                    </ButtonLink>
                </div>
            </div>
            <div className='testSHSATReport__card'>
                <div className='testSHSATReport__cardRow'>
                    {/* Horizontal score band across the top: TOTAL SCORE +
                        per-section scores side by side. */}
                    <div className='testSHSATReport__scoresGroup'>
                        <div className='testSHSATReport__scoreBlock'>
                        <h2 className='testSHSATReport__scoreTitle' style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            TOTAL SCORE
                            {/* Tutor-only: estimated scaled score behind a
                                "Scaled Score" button. The headline is the RAW
                                score; the scaled value is an estimate because
                                the official test removes an unknown number of
                                unscored field questions. Shown for BOTH adaptive
                                and legacy — legacy maps its raw correct through
                                the same Kenny-Tan curve (see ``scaledScore``). */}
                            {
                                <span style={{ position: 'relative', display: 'inline-flex' }}>
                                    <button
                                        type='button'
                                        onClick={() => setShowScaledInfo(v => !v)}
                                        style={{
                                            cursor: 'pointer', padding: '4px 10px', borderRadius: 6,
                                            border: '1px solid ' + (showScaledInfo ? '#2f6fd1' : '#bbb'),
                                            background: showScaledInfo ? '#2f6fd1' : '#fff',
                                            color: showScaledInfo ? '#fff' : '#333',
                                            fontSize: 13, fontWeight: 600,
                                        }}
                                    >
                                        Scaled Score
                                    </button>
                                    {showScaledInfo &&
                                        <div style={{
                                            position: 'absolute', top: 34, left: 0, zIndex: 10,
                                            width: 380, maxHeight: 520, overflowY: 'auto',
                                            padding: '14px 16px', textAlign: 'left',
                                            background: '#fff', border: '1px solid #ccc', borderRadius: 8,
                                            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                                            fontSize: 13, fontWeight: 400, lineHeight: 1.5, color: '#333',
                                        }}>
                                            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10 }}>Estimated scaled score</div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                                                <span>Total</span>
                                                <span style={{ fontWeight: 700 }}>{Math.round(Number(testData.scaledScore))} / {testData.testSections.length * 350}</span>
                                            </div>
                                            {testData.testSections.map(s => (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                                                    <span>{s.subject}</span>
                                                    <span style={{ fontWeight: 700 }}>{Math.round(Number(s.scaledScore))} / 350</span>
                                                </div>
                                            ))}
                                            <p style={{ marginTop: 12, marginBottom: 0, fontSize: 13, color: '#444' }}>
                                                These are <strong>estimated</strong> scores based on a recalibrated scale to
                                                accommodate for the new changes to the SHSAT including changes to the number of
                                                questions. We will continuously recalibrate our scale as we collect more data and
                                                information.
                                            </p>
                                            <div style={{ fontWeight: 700, margin: '14px 0 6px' }}>Raw &rarr; Scaled (per section)</div>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                                <tbody>
                                                    <tr style={{ fontWeight: 700, borderBottom: '1px solid #ddd' }}>
                                                        <td style={{ padding: '3px 6px' }}>Raw</td>
                                                        <td style={{ padding: '3px 6px' }}>Scaled</td>
                                                        <td style={{ padding: '3px 6px', borderLeft: '1px solid #eee' }}>Raw</td>
                                                        <td style={{ padding: '3px 6px' }}>Scaled</td>
                                                    </tr>
                                                    {Array.from({ length: scaledConvHalf }).map((_, i) => {
                                                        const right = i + scaledConvHalf;
                                                        return (
                                                            <tr key={i}>
                                                                <td style={{ padding: '2px 6px' }}>{i}</td>
                                                                <td style={{ padding: '2px 6px' }}>{scaledConv[i]}</td>
                                                                <td style={{ padding: '2px 6px', borderLeft: '1px solid #eee' }}>{right <= scaledConvMax ? right : ''}</td>
                                                                <td style={{ padding: '2px 6px' }}>{right <= scaledConvMax ? scaledConv[right] : ''}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    }
                                </span>
                            }
                        </h2>
                        {/* Headline = RAW score (correct / total). */}
                        <div className='testSHSATReport__scorePercent'>
                            {testData.isAdaptive ? (testData.rawCorrect ?? 0) : Math.round(Number(testData.score))}
                            <span style={{fontSize: '0.5em', fontWeight: 400}}>/{testData.totalQuestionCount}</span>
                        </div>
                        </div>
                        {testData.testSections.map((testSection, t) => (
                            <div className='testSHSATReport__scoreBlock' key={t}>
                                <h3 className='testSHSATReport__sectionScoreTitle'>
                                    {testSection.subject}
                                </h3>
                                <div className='testSHSATReport__scorePercent'>
                                    {testData.isAdaptive ? (testSection.rawCorrect ?? 0) : Math.round(Number(testSection.score))}
                                    <span style={{fontSize: '0.5em', fontWeight: 400}}>/{testSection.questionCount}</span>
                                </div>
                                <div style={{fontSize: 16, marginTop: 6, color: 'var(--gray500)'}}>
                                    Time: {formatTime(testData.testModules.filter(testModule => (
                                        testModule.subject === testSection.subject
                                    )).reduce((total, testModule) => (
                                        total + testModule.answers.reduce((time, answer) => time + answer.timeSpent, 0)
                                    ), 0))}
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Right column: Raw Scores tables, then Category Scores
                        domain graphs — same as the SAT report. */}
                    <div className='testSHSATReport__overview'>
                        {testData.testSections.some(s => s.rootCategories && s.rootCategories.length > 0) && (<>
                            <h2 className='testSHSATReport__cardSubTitle testSHSATReport__categoryHeading'>
                                Category Scores
                            </h2>
                            <div className='testSHSATReport__overviewRow testSHSATReport__categoryRow'>
                                {testData.testSections.map((testSection, s) => (
                                    <div className='testSHSATReport__overviewItem'>
                                        <h4 className='testSHSATReport__overviewLabel'>
                                            {testSection.subject}
                                        </h4>
                                        {testSection.rootCategories.map(rootCategory => (<>
                                            <h5
                                                className={rootCategoryName === rootCategory.name
                                                    ? 'testSHSATReport__levelLabel--active'
                                                    : 'testSHSATReport__levelLabel'
                                                }
                                                onClick={() => setRootCategoryName(rootCategoryName === rootCategory.name ? '' : rootCategory.name)}
                                            >
                                                {rootCategory.name}
                                                <div className={rootCategoryName === rootCategory.name
                                                    ? 'testSHSATReport__levelDetailed--active'
                                                    : 'testSHSATReport__levelDetailed'
                                                }>
                                                    {(rootCategory.categories ?? []).map(category => (
                                                        <div className='testSHSATReport__levelDetailedItem'>
                                                            <p>{category.name}</p>
                                                            <p className={category.numCorrect/category.numQuestions >= 0.8
                                                                ? 'testSHSATReport__levelDetailedGreen'
                                                                : category.numCorrect/category.numQuestions >= 0.4
                                                                ? 'testSHSATReport__levelDetailedYellow'
                                                                : 'testSHSATReport__levelDetailedRed'
                                                            }>
                                                                {category.numCorrect}/{category.numQuestions}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </h5>
                                            <LevelBar
                                                className={s % 2 === 0 ? 'testSHSATReport__levelGreen' : 'testSHSATReport__levelOrange'}
                                                percent={rootCategory.numQuestions ? 100*rootCategory.numCorrect/rootCategory.numQuestions : 0}
                                                label={`${rootCategory.numCorrect}/${rootCategory.numQuestions}`}
                                            />
                                        </>))}
                                    </div>
                                ))}
                            </div>
                        </>)}
                    </div>
                </div>
                {testData.testSections.some(s => s.rootCategories && s.rootCategories.length > 0) && (<>
                {/* Grey divider + extra top spacing between Category Scores
                    and Section Time Averages. */}
                <div className='testSHSATReport__timeDivider' />
                <h2 className='testSHSATReport__cardSubTitle testSHSATReport__timeHeading'>
                    Section Time Averages
                </h2>
                <div className='testSHSATReport__overviewRow testSHSATReport__timeRow'>
                    {testData.testSections.map(testSection => (
                        <div className='testSHSATReport__overviewItem'>
                            <h4 className='testSHSATReport__overviewLabel'>
                                {testSection.subject}
                            </h4>
                            <table className='testSHSATReport__timeTable'>
                                <tbody>
                                    <tr>
                                        <th></th>
                                        <th>Section Time</th>
                                        <th>Time Per Question</th>
                                    </tr>
                                    {testSection.rootCategories.map(rootCategory => (
                                        <tr>
                                            <td>
                                                {rootCategory.name}
                                            </td>
                                            <td>
                                                {formatTime2(rootCategory.timeSpent)}
                                            </td>
                                            <td>
                                                <GaugeComponent
                                                    className='testSHSATReport__timeGauge'
                                                    type='semicircle'
                                                    value={rootCategory.numQuestions ? rootCategory.timeSpent/rootCategory.numQuestions : 0}
                                                    minValue={0}
                                                    maxValue={timePerQuestionFull * 3}
                                                    arc={{
                                                        'subArcs': [
                                                            { length: 0.33, color: "#5BE12C"},
                                                            { length: 0.33, color: "#F5CD19"},
                                                            { length: 0.33, color: "#EA4228"}
                                                        ]
                                                    }}
                                                    pointer={{
                                                        'width': 10,
                                                    }}
                                                    labels={{
                                                        'valueLabel': {
                                                            'hide': true
                                                        },
                                                        'tickLabels': {
                                                            'hideMinMax': true
                                                        }
                                                    }}
                                                />
                                                <p>{formatTime2(rootCategory.numQuestions ? rootCategory.timeSpent/rootCategory.numQuestions : 0)}</p>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
                {/*
                    PRINT ONLY. The two blocks above list every category twice
                    -- once for its score, once for its timing. On paper that
                    is double the rows for the same information, which is what
                    stopped this report fitting a page. This renders them
                    merged, one row per category, the way the ACT report does
                    on screen and in print. Same components and same data
                    expressions as above, so the two cannot disagree on a
                    value. The @media print rules swap these.
                */}
                <div className='testSHSATReport__combinedPrint'>
                    {testData.testSections.map((testSection, s) => (
                        <div
                            className='testSHSATReport__combinedPrintSection'
                            key={`combined:${testSection.subject}`}
                        >
                            <h4 className='testSHSATReport__overviewLabel'>
                                {testSection.subject}
                            </h4>
                            <table className='testSHSATReport__combinedTable'>
                                <tbody>
                                    <tr>
                                        <th>By Category</th>
                                        <th>Section Time</th>
                                        <th>Time Per Question</th>
                                    </tr>
                                    {/* `?? []` because the guard above only requires SOME
                                        section to have categories -- a section with none
                                        would otherwise throw on .map. */}
                                    {(testSection.rootCategories ?? []).map(rootCategory => (
                                        <tr key={`combined:${testSection.subject}:${rootCategory.name}`}>
                                            <td>
                                                <p className='testSHSATReport__combinedCategory'>
                                                    {rootCategory.name}
                                                </p>
                                                <LevelBar
                                                    className={s % 2 === 0
                                                        ? 'testSHSATReport__levelGreen'
                                                        : 'testSHSATReport__levelOrange'
                                                    }
                                                    percent={rootCategory.numQuestions
                                                        ? 100 * rootCategory.numCorrect / rootCategory.numQuestions
                                                        : 0
                                                    }
                                                    label={`${rootCategory.numCorrect}/${rootCategory.numQuestions}`}
                                                />
                                            </td>
                                            <td>{formatTime2(rootCategory.timeSpent)}</td>
                                            <td>
                                                {formatTime2(rootCategory.numQuestions
                                                    ? rootCategory.timeSpent / rootCategory.numQuestions
                                                    : 0
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
                </>)}
            </div>
            <div className='testSHSATReport__printBreak'></div>
            {/* <h2 className='testSHSATReport__subHeader'>
                Test result detailed breakdown
            </h2> */}
            <div className='testSHSATReport__questions'>
                <h2 className='testSHSATReport__questionsHeader'>
                    Questions Overview
                </h2>
                <div className='testSHSATReport__sectionTabs'>
                    {testData.testSections.map((testSection, t) => (
                        <button
                            className={sectionIndex === t
                                ? 'testSHSATReport__sectionTab--active'
                                : 'testSHSATReport__sectionTab'
                            }
                            onClick={() => {
                                setSectionIndex(t);
                                setModuleIndex(0);
                            }}
                            key={testSection.subject}
                        >
                            {testSection.subject}
                        </button>
                    ))}
                    <div className={sectionIndex === 0
                        ? 'testSHSATReport__sectionTabIndicatorLeft'
                        : 'testSHSATReport__sectionTabIndicatorRight'
                    }></div>
                </div>
                {testData.testModules.filter(testModule => (
                    testModule.subject === testData.testSections[sectionIndex].subject
                )).length > 1 &&
                    <div className='testSHSATReport__moduleTabs'>
                        {testData.testModules.filter(testModule => (
                            testModule.subject === testData.testSections[sectionIndex].subject
                        )).map((testModule, t) => (
                            <button
                                className={moduleIndex === t
                                    ? 'testSHSATReport__moduleTab--active'
                                    : 'testSHSATReport__moduleTab'
                                }
                                onClick={() => setModuleIndex(t)}
                                key={testModule.name}
                            >
                                Module {testModule.moduleNumber} {testModule.moduleNumber === 2 && (testModule.hard ? '(Hard)' : '(Easy)')}
                            </button>
                        ))}
                        <div className={moduleIndex === 0
                            ? 'testSHSATReport__moduleTabIndicatorLeft'
                            : 'testSHSATReport__moduleTabIndicatorRight'
                        }></div>
                    </div>
                }
                
                <div className='testSHSATReport__questionsOverview'>
                    <div className='testSHSATReport__questionsSummary'>
                        <div className='testSHSATReport__questionsSummaryItem'>
                            <p className='testSHSATReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.length
                                ), 0)}
                            </p>
                            <p className='testSHSATReport__questionSummaryText'>
                                Total Questions
                            </p>
                        </div>
                        <div className='testSHSATReport__questionsSummaryItem'>
                            <p className='testSHSATReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.filter(answer => answer.isCorrect).length
                                ), 0)}
                            </p>
                            <p className='testSHSATReport__questionSummaryText'>
                                Correct Answers
                            </p>
                        </div>
                        <div className='testSHSATReport__questionsSummaryItem'>
                            <p className='testSHSATReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.filter(answer => !answer.isCorrect).length
                                ), 0)}
                            </p>
                            <p className='testSHSATReport__questionSummaryText'>
                                Incorrect Answers
                            </p>
                        </div>
                    </div>
                    <table className='testSHSATReport__questionsTable'>
                        <tbody>
                            {questionTableHead}
                            {currentModule?.answers.map(answer => (
                                questionRow(currentModule, answer)
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {/*
                Print-only: the whole test, section by section and module by
                module. The on-screen table above shows one module at a time,
                which is right on screen and wrong on paper. The @media print
                rules swap these two -- for a single-report print only. A bulk
                print hides this block (BulkPrintReports/index.css).
            */}
            <div className='testSHSATReport__questionsPrint'>
                {modulesInOrder.map(testModule => (
                    <div
                        className='testSHSATReport__questionsPrintModule'
                        key={`print:${testModule.name}`}
                    >
                        <p className='testSHSATReport__moduleName'>
                            {moduleLabel(testModule)}
                        </p>
                        <div className='testSHSATReport__questionsOverview'>
                            {/* No Total/Correct/Incorrect summary on paper: its
                                height goes to the rows instead, so a full module
                                fits one sheet at a readable size. The on-screen
                                table keeps it. */}
                            <table className='testSHSATReport__questionsTable'>
                                {/* <thead> so the header repeats on every sheet a
                                    50-row module spans. The screen table keeps its
                                    header inside <tbody>. */}
                                <thead>
                                    {questionTableHead}
                                </thead>
                                <tbody>
                                    {testModule.answers.map(answer => (
                                        questionRow(testModule, answer)
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default testSHSATReport;